import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys, verydia } from './api.ts';

/**
 * Reads and writes for the identity surface.
 *
 * Every mutation invalidates exactly the keys its change affects. A blanket
 * `invalidateQueries()` would be one line shorter and would refetch the audit
 * log, the organization list and the platform user list every time somebody
 * changes a role.
 */

export function useMembers(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.members(organizationId ?? ''),
    queryFn: () => verydia.listMembers(organizationId!),
    enabled: organizationId !== null,
    retry: false,
  });
}

export function useInvitations(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.invitations(organizationId ?? ''),
    queryFn: () => verydia.listInvitations(organizationId!),
    enabled: organizationId !== null,
    retry: false,
  });
}

export function useOrganizations(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.organizations,
    queryFn: () => verydia.listOrganizations(),
    enabled,
    retry: false,
  });
}

export function usePlatformUsers(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.platformUsers,
    queryFn: () => verydia.listPlatformUsers(),
    enabled,
    retry: false,
  });
}

export function usePlatformAudit(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.platformAudit,
    queryFn: () => verydia.listPlatformAudit(),
    enabled,
    retry: false,
  });
}

export function useOrganizationAudit(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.organizationAudit(organizationId ?? ''),
    queryFn: () => verydia.listOrganizationAudit(organizationId!),
    enabled: organizationId !== null,
    retry: false,
  });
}

export function useInviteMember(organizationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: string }) =>
      verydia.inviteMember(organizationId, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.invitations(organizationId) });
      void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
    },
  });
}

export function useRevokeInvitation(organizationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => verydia.revokeInvitation(organizationId, invitationId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.invitations(organizationId) });
      void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
    },
  });
}

export function useChangeMemberRole(organizationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { membershipId: string; role: string }) =>
      verydia.changeMemberRole(organizationId, input.membershipId, input.role),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.members(organizationId) });
      void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
      // A role change can be the caller's own, which changes what they may do.
      void client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useSetMemberStatus(organizationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { membershipId: string; intent: 'suspend' | 'reactivate' | 'remove' }) =>
      verydia.setMemberStatus(organizationId, input.membershipId, input.intent),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.members(organizationId) });
      void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
      void client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useCreateOrganization() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { display_name: string; slug?: string }) =>
      verydia.createOrganization(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.organizations });
      void client.invalidateQueries({ queryKey: queryKeys.platformAudit });
    },
  });
}

export function useSeedOrganizationOwner() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { organizationId: string; email: string }) =>
      verydia.seedOrganizationOwner(input.organizationId, input.email),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.organizations });
      void client.invalidateQueries({ queryKey: queryKeys.platformAudit });
      // The caller may have just made themselves a member, which changes the
      // organization switcher and every permission the console renders from.
      void client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useSetPlatformUserStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; intent: 'suspend' | 'reactivate' }) =>
      verydia.setPlatformUserStatus(input.userId, input.intent),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.platformUsers });
      void client.invalidateQueries({ queryKey: queryKeys.platformAudit });
    },
  });
}

export function useGrantPlatformRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; role: string }) =>
      verydia.grantPlatformRole(input.userId, input.role),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.platformUsers });
      void client.invalidateQueries({ queryKey: queryKeys.platformAudit });
      void client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

/* ---------------------------------- catalog -------------------------------- */

/**
 * Organization-wide catalog totals.
 *
 * Enabled only once an organization is known, like every other tenant-scoped
 * read — a summary fetched before authentication would be the startup-401
 * regression in a new place.
 */
export function useCatalogSummary(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.catalogSummary(organizationId ?? ''),
    queryFn: () => verydia.getCatalogSummary(organizationId!),
    enabled: organizationId !== null,
    retry: false,
  });
}

export function useProducts(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.products(organizationId ?? ''),
    queryFn: () => verydia.listProducts(organizationId!),
    enabled: organizationId !== null,
    retry: false,
  });
}

export function useProduct(organizationId: string | null, productId: string | null) {
  return useQuery({
    queryKey: queryKeys.product(organizationId ?? '', productId ?? ''),
    queryFn: () => verydia.getProduct(organizationId!, productId!),
    enabled: organizationId !== null && productId !== null,
    retry: false,
  });
}

/**
 * One product's own history.
 *
 * Filtered on the server by resource, not fetched wholesale and narrowed here:
 * client-side filtering would page through unrelated events and quietly show a
 * short list because the interesting ones fell off page one.
 */
export function useProductActivity(organizationId: string | null, productId: string | null) {
  return useQuery({
    queryKey: queryKeys.productActivity(organizationId ?? '', productId ?? ''),
    queryFn: () =>
      verydia.listOrganizationAudit(organizationId!, { type: 'product', id: productId! }),
    enabled: organizationId !== null && productId !== null,
    retry: false,
  });
}

/* -------------------------------- environments ----------------------------- */

export function useEnvironments(organizationId: string | null, productId: string | null) {
  return useQuery({
    queryKey: queryKeys.environments(organizationId ?? '', productId ?? ''),
    queryFn: () => verydia.listEnvironments(organizationId!, productId!),
    enabled: organizationId !== null && productId !== null,
    retry: false,
  });
}

/**
 * Everything an environment mutation has to refresh.
 *
 * Four surfaces read this data: the environment table, the product's own
 * environment count, the same count in the products list, and the context
 * selector at the top of every page. Missing one leaves a stale zero next to a
 * populated table, which is the placeholder-lie class in a new place.
 */
function invalidateEnvironments(
  client: ReturnType<typeof useQueryClient>,
  organizationId: string,
  productId: string,
): void {
  void client.invalidateQueries({ queryKey: queryKeys.environments(organizationId, productId) });
  void client.invalidateQueries({ queryKey: queryKeys.product(organizationId, productId) });
  void client.invalidateQueries({ queryKey: queryKeys.products(organizationId) });
  // Any catalog mutation moves the Overview totals with it.
  void client.invalidateQueries({ queryKey: queryKeys.catalogSummary(organizationId) });
  void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
  void client.invalidateQueries({
    queryKey: queryKeys.productActivity(organizationId, productId),
  });
}

export function useCreateEnvironment(organizationId: string, productId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; kind: string; classification: string; slug?: string }) =>
      verydia.createEnvironment(organizationId, productId, input),
    onSuccess: () => invalidateEnvironments(client, organizationId, productId),
  });
}

export function useUpdateEnvironment(
  organizationId: string,
  productId: string,
  environmentId: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      expected_version: number;
      name?: string;
      kind?: string;
      classification?: string;
    }) => verydia.updateEnvironment(organizationId, productId, environmentId, input),
    onSuccess: () => invalidateEnvironments(client, organizationId, productId),
  });
}

export function useArchiveEnvironment(
  organizationId: string,
  productId: string,
  environmentId: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { expected_version: number }) =>
      verydia.archiveEnvironment(organizationId, productId, environmentId, input.expected_version),
    onSuccess: () => invalidateEnvironments(client, organizationId, productId),
  });
}

/* ------------------------- licensed product plans -------------------------- */

export function useLicensedProductPlans(organizationId: string | null, productId: string | null) {
  return useQuery({
    queryKey: queryKeys.plans(organizationId ?? '', productId ?? ''),
    queryFn: () => verydia.listLicensedProductPlans(organizationId!, productId!),
    enabled: organizationId !== null && productId !== null,
    retry: false,
  });
}

/** Every surface a plan mutation has to refresh. Missing one leaves a stale count. */
function invalidatePlans(
  client: ReturnType<typeof useQueryClient>,
  organizationId: string,
  productId: string,
): void {
  void client.invalidateQueries({ queryKey: queryKeys.plans(organizationId, productId) });
  void client.invalidateQueries({ queryKey: queryKeys.product(organizationId, productId) });
  void client.invalidateQueries({ queryKey: queryKeys.products(organizationId) });
  // Any catalog mutation moves the Overview totals with it.
  void client.invalidateQueries({ queryKey: queryKeys.catalogSummary(organizationId) });
  void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
  void client.invalidateQueries({
    queryKey: queryKeys.productActivity(organizationId, productId),
  });
}

export function useCreateLicensedProductPlan(organizationId: string, productId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      code?: string;
      description?: string | null;
      list_price_minor?: number | null;
      currency?: string | null;
      billing_interval?: string | null;
    }) => verydia.createLicensedProductPlan(organizationId, productId, input),
    onSuccess: () => invalidatePlans(client, organizationId, productId),
  });
}

export function useUpdateLicensedProductPlan(
  organizationId: string,
  productId: string,
  planId: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      expected_version: number;
      name?: string;
      description?: string | null;
      list_price_minor?: number | null;
      currency?: string | null;
      billing_interval?: string | null;
    }) => verydia.updateLicensedProductPlan(organizationId, productId, planId, input),
    onSuccess: () => invalidatePlans(client, organizationId, productId),
  });
}

export function useActivateLicensedProductPlan(
  organizationId: string,
  productId: string,
  planId: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { expected_version: number }) =>
      verydia.activateLicensedProductPlan(
        organizationId,
        productId,
        planId,
        input.expected_version,
      ),
    onSuccess: () => invalidatePlans(client, organizationId, productId),
  });
}

export function useArchiveLicensedProductPlan(
  organizationId: string,
  productId: string,
  planId: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { expected_version: number }) =>
      verydia.archiveLicensedProductPlan(organizationId, productId, planId, input.expected_version),
    onSuccess: () => invalidatePlans(client, organizationId, productId),
  });
}

export function useArchiveProduct(organizationId: string, productId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { expected_version: number }) =>
      verydia.archiveProduct(organizationId, productId, input.expected_version),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.products(organizationId) });
      // Any catalog mutation moves the Overview totals with it.
      void client.invalidateQueries({ queryKey: queryKeys.catalogSummary(organizationId) });
      void client.invalidateQueries({ queryKey: queryKeys.product(organizationId, productId) });
      void client.invalidateQueries({
        queryKey: queryKeys.productActivity(organizationId, productId),
      });
      void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
    },
  });
}

export function useCreateProduct(organizationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      distribution: string;
      slug?: string;
      description?: string | null;
    }) => verydia.createProduct(organizationId, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.products(organizationId) });
      // Any catalog mutation moves the Overview totals with it.
      void client.invalidateQueries({ queryKey: queryKeys.catalogSummary(organizationId) });
      // The product context selector and the Overview count both read products.
      // Leaving either stale is the placeholder-lie class of defect.
      void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
    },
  });
}

export function useUpdateProduct(organizationId: string, productId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      expected_version: number;
      name?: string;
      description?: string | null;
      distribution?: string;
    }) => verydia.updateProduct(organizationId, productId, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.products(organizationId) });
      // Any catalog mutation moves the Overview totals with it.
      void client.invalidateQueries({ queryKey: queryKeys.catalogSummary(organizationId) });
      void client.invalidateQueries({ queryKey: queryKeys.product(organizationId, productId) });
      void client.invalidateQueries({
        queryKey: queryKeys.productActivity(organizationId, productId),
      });
      void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
    },
  });
}

/* -------------------------------- customers --------------------------------- */

/**
 * Customers, organization-scoped.
 *
 * No product parameter, and deliberately so: a customer is a sibling of Product,
 * not a descendant. Scoping this list by the selected product would invent a
 * relationship the model does not have.
 */
export function useCustomers(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.customers(organizationId ?? ''),
    queryFn: () => verydia.listCustomers(organizationId!),
    enabled: organizationId !== null,
    retry: false,
  });
}

function invalidateCustomers(client: ReturnType<typeof useQueryClient>, organizationId: string) {
  void client.invalidateQueries({ queryKey: queryKeys.customers(organizationId) });
  // The Overview counts active customers, and the audit feed gains a row.
  void client.invalidateQueries({ queryKey: queryKeys.catalogSummary(organizationId) });
  void client.invalidateQueries({ queryKey: queryKeys.organizationAudit(organizationId) });
}

export function useCreateCustomer(organizationId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      contact_email?: string | null;
      external_reference?: string | null;
    }) => verydia.createCustomer(organizationId, input),
    onSuccess: () => invalidateCustomers(client, organizationId),
  });
}

export function useUpdateCustomer(organizationId: string, customerId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      expected_version: number;
      name?: string;
      contact_email?: string | null;
      external_reference?: string | null;
    }) => verydia.updateCustomer(organizationId, customerId, input),
    onSuccess: () => invalidateCustomers(client, organizationId),
  });
}

export function useArchiveCustomer(organizationId: string, customerId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (expectedVersion: number) =>
      verydia.archiveCustomer(organizationId, customerId, expectedVersion),
    onSuccess: () => invalidateCustomers(client, organizationId),
  });
}
